if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.submitButton.dataset.requiresVariantSelection === 'true') {
          this.submitButton.setAttribute('disabled', 'disabled');
          const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
          if (variantSelects) {
            variantSelects.querySelectorAll('fieldset input[type="radio"]:checked').forEach((radio) => {
              radio.checked = false;
            });
          }
        }

        if (this.dataset.sampleProduct === 'true') {
          this.checkSampleLimitOnLoad();
        }
      }

      checkSampleLimitOnLoad() {
        const productId = parseInt(this.form.dataset.productId);
        fetch(`${routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            const existingQty = cart.items
              .filter((item) => item.product_id === productId)
              .reduce((sum, item) => sum + item.quantity, 0);
            const quantityInput = this.closest('product-info')?.querySelector('quantity-input .quantity__input');
            const maxAllowed = 2 - existingQty;

            if (quantityInput) {
              quantityInput.max = maxAllowed > 0 ? maxAllowed : 0;
              if (parseInt(quantityInput.value) > maxAllowed) {
                quantityInput.value = maxAllowed > 0 ? maxAllowed : 1;
                quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }

            if (existingQty >= 2) {
              this.submitButton.setAttribute('disabled', 'disabled');
              this.handleErrorMessage(this.dataset.sampleLimitReached);
            }
          })
          .catch(() => {});
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (this.dataset.sampleProduct === 'true') {
          this.checkSampleLimitAndAdd(evt);
          return;
        }

        this.proceedWithAdd(evt);
      }

      checkSampleLimitAndAdd(evt) {
        const formData = new FormData(this.form);
        const productId = parseInt(this.form.dataset.productId);
        const quantityToAdd = parseInt(formData.get('quantity') || 1);

        fetch(`${routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            const existingQty = cart.items
              .filter((item) => item.product_id === productId)
              .reduce((sum, item) => sum + item.quantity, 0);

            if (existingQty + quantityToAdd > 2) {
              const remaining = 2 - existingQty;
              if (remaining <= 0) {
                this.handleErrorMessage(this.dataset.sampleLimitReached);
              } else {
                this.handleErrorMessage(this.dataset.sampleLimitRemaining.replace('{{ count }}', remaining));
              }
              return;
            }

            this.proceedWithAdd(evt);
          })
          .catch(() => {
            this.proceedWithAdd(evt);
          });
      }

      proceedWithAdd(evt) {
        // fc-product-dropdown-v1 template: the dropdown choices REPLACE the page
        // product — the variant id is taken from the selections at submit time, never
        // from the page product (matched to this form by id so quick-add forms
        // elsewhere on the page never bundle by accident).
        // getAttribute('id') is required: the form's <input name="id"> shadows
        // form.id via [LegacyOverrideBuiltIns], returning the element, not the string.
        const fcDropdowns = document.querySelector(
          '.pdp-dropdowns[data-product-form-id="' + this.form.getAttribute('id') + '"]'
        );
        if (fcDropdowns) {
          // One combination per cart: pdp-dropdown.js keeps the button disabled
          // while tagged lines are in the cart; this guard also covers submits
          // triggered before the async cart check resolves.
          if (fcDropdowns.dataset.fcLocked === 'true') return;
          const chosen = Array.from(
            fcDropdowns.querySelectorAll('[data-dropdown-option][aria-selected="true"]')
          ).filter((option) => !option.disabled && option.dataset.variantId);
          if (!chosen.length) {
            // Nothing purchasable selected — never fall back to the page product.
            this.handleErrorMessage(window.variantStrings.soldOut);
            return;
          }
          // Tag the line(s) with the page product id so pdp-dropdown.js can tell
          // "added from this page" apart from the same products bought standalone.
          // Underscore-prefixed properties stay hidden in cart, drawer and checkout.
          let lockProp = this.form.querySelector('input[name="properties[_fc_dropdown]"]');
          if (!lockProp) {
            lockProp = document.createElement('input');
            lockProp.type = 'hidden';
            lockProp.name = 'properties[_fc_dropdown]';
            this.form.appendChild(lockProp);
          }
          lockProp.value = this.form.dataset.productId;
          // Visible "Pakket: …" property on both lines so the customer can tell the
          // combination lines apart from the same products added standalone (shows
          // in drawer, cart and checkout). Name/value come from the wrapper's
          // locale + metafield-driven data attributes.
          if (fcDropdowns.dataset.packPropertyName && fcDropdowns.dataset.packPropertyValue) {
            let packProp = this.form.querySelector('input[data-fc-pack-property]');
            if (!packProp) {
              packProp = document.createElement('input');
              packProp.type = 'hidden';
              packProp.setAttribute('data-fc-pack-property', '');
              packProp.name = 'properties[' + fcDropdowns.dataset.packPropertyName + ']';
              this.form.appendChild(packProp);
            }
            packProp.value = fcDropdowns.dataset.packPropertyValue;
          }
          const idInput = this.form.querySelector('input[name="id"]');
          if (idInput) {
            idInput.value = chosen[0].dataset.variantId;
            idInput.disabled = false;
          }
          if (chosen.length > 1) {
            const quantity = parseInt(new FormData(this.form).get('quantity'), 10) || 1;
            this.addBundleToCart(evt, chosen[1].dataset.variantId, quantity);
            return;
          }
          this.addSingleToCart(evt);
          return;
        }

        const mainOption = document.querySelector('[data-option].active');
        const optionValue = mainOption && mainOption.getAttribute('data-option');

        if (optionValue === 'bundle') {
          const activeBundleOption = document.querySelector('[data-bundle-option].active');
          const bundleRole = activeBundleOption && activeBundleOption.getAttribute('data-bundle-option');
          const secondVariantId = this.getSecondVariantIdForBundle();
          if (secondVariantId) {
            this.addBundleToCart(evt, secondVariantId, undefined, bundleRole);
            return;
          }
          if (bundleRole === 'trial-kit') {
            // Trial Kit is a two-product bundle — never add the main product alone.
            // Explain the block and steer the customer straight into the picker.
            this.handleErrorMessage(window.variantStrings.trialKitPartnerRequired);
            const addBundleButton = document.querySelector('[data-add-bundle-button]');
            if (addBundleButton) addBundleButton.click();
            return;
          }
        }
        this.addSingleToCart(evt);
      }

      getSecondVariantIdForBundle() {
        const formProductId = this.form.dataset.productId;
        const activeBundleOption = document.querySelector('[data-bundle-option].active');
        const bundleOption = activeBundleOption && activeBundleOption.getAttribute('data-bundle-option');

        if (bundleOption === 'trial-kit') {
          const bundleVariantId = typeof localStorage !== 'undefined' && localStorage.getItem('bundleVariantId');
          const bundleMainProductId = typeof localStorage !== 'undefined' && localStorage.getItem('bundleMainProductId');
          if (bundleVariantId && bundleMainProductId && formProductId === bundleMainProductId) return bundleVariantId;
        }
        if (bundleOption === 'combi-kit' && activeBundleOption) {
          const variantId = activeBundleOption.getAttribute('data-combi-kit-variant-id');
          if (variantId) return variantId;
        }
        return null;
      }

      addBundleToCart(evt, secondVariantId, secondQuantity, bundleRole) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        // The bundle discount function (mrgreenmind-bundle-discounts app) pairs
        // cart lines via these hidden _fc_bundle properties — without them the
        // pair gets no checkout discount.
        if (bundleRole) formData.append('properties[_fc_bundle]', bundleRole);
        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((firstResponse) => {
            if (firstResponse.status) {
              this.handleAddError(firstResponse, formData.get('id'));
              return Promise.reject();
            }
            const bundleFormData = new FormData();
            bundleFormData.append('id', secondVariantId);
            bundleFormData.append('quantity', secondQuantity || 1);
            if (bundleRole) bundleFormData.append('properties[_fc_bundle]', 'partner');
            // fc dropdown template: the second line carries the same lock tag and
            // visible pack property as the first (added to the form as hidden
            // inputs in proceedWithAdd).
            const fcDropdownProp = formData.get('properties[_fc_dropdown]');
            if (fcDropdownProp) bundleFormData.append('properties[_fc_dropdown]', fcDropdownProp);
            const fcPackInput = this.form.querySelector('input[data-fc-pack-property]');
            if (fcPackInput && fcPackInput.value) bundleFormData.append(fcPackInput.name, fcPackInput.value);
            if (this.cart) {
              bundleFormData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
              bundleFormData.append('sections_url', window.location.pathname);
            }
            const bundleConfig = fetchConfig('javascript');
            bundleConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete bundleConfig.headers['Content-Type'];
            bundleConfig.body = bundleFormData;
            return fetch(`${routes.cart_add_url}`, bundleConfig).then((res) => res.json());
          })
          .then((response) => {
            if (!response || response.status) {
              if (response && response.status) this.handleAddError(response, secondVariantId);
              return;
            }
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
            const formData = new FormData(this.form);
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            CartPerformance.measure("add:paint-updated-sections", () => {
              this.cart.renderContents(response);
            });
          })
          .catch((e) => {
            if (e) console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      addSingleToCart(evt) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleAddError(response, variantId) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: variantId,
          errors: response.errors || response.description,
          message: response.message,
        });
        this.handleErrorMessage(response.description);
        this.error = true;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        // Desktop span (submitButtonText) carries the full label; the mobile span
        // shows the short label for the standard state and state texts verbatim.
        const mobileLabel = this.submitButton.querySelector('.product-form__label--mobile');
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) {
            this.submitButtonText.textContent = text;
            if (mobileLabel) mobileLabel.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          if (mobileLabel) mobileLabel.textContent = mobileLabel.dataset.shortLabel || window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
